import { useEffect, useRef, useState } from 'react';
import { ChevronDown, UtensilsCrossed, ShoppingBag, Apple, Scissors, Shirt, Footprints, Network, Building2, Store, ShieldCheck, TrendingUp, Coins } from 'lucide-react';
import { useLanguage } from '@/lib/LanguageContext';
import CountUp from '@/components/smartpos/CountUp';

const HERO_VIDEO = 'https://media.base44.com/videos/public/6a877e598e2aae01ac439787/852c5c98e_hiopos-hosteleria_PZ1T6JQm.mp4';

export default function Hero() {
  const { t } = useLanguage();
  const [playVideo, setPlayVideo] = useState(false);
  const [currentSector, setCurrentSector] = useState(0);
  const videoRef = useRef(null);

  // Sectores y cifras del hero: se ven solo en móvil, donde el vídeo necesita contexto.
  const sectors = [
    { name: t('sectors.hospitality.name'), icon: UtensilsCrossed },
    { name: t('sectors.retail.name'), icon: ShoppingBag },
    { name: t('sectors.food.name'), icon: Apple },
    { name: t('sectors.beauty.name'), icon: Scissors },
    { name: t('sectors.fashion.name'), icon: Shirt },
    { name: t('sectors.shoes.name'), icon: Footprints },
    { name: t('sectors.chains.name'), icon: Network },
    { name: t('sectors.franchises.name'), icon: Building2 },
  ];

  const stats = [
    { value: '+500', label: t('hero.stats.businesses'), icon: Store, color: '#0ff0b3' },
    { value: '99.9%', label: t('hero.stats.uptime'), icon: ShieldCheck, color: '#0095fc' },
    { value: '+30%', label: t('hero.stats.sales'), icon: TrendingUp, color: '#7800f7' },
    { value: '0%', label: t('hero.stats.cash'), icon: Coins, color: '#0ff0b3' },
  ];

  useEffect(() => {
    const id = setInterval(() => setCurrentSector((i) => (i + 1) % sectors.length), 2500);
    return () => clearInterval(id);
  }, []);

  // El vídeo del hero pesa ~24 MB. Si se pide durante la carga, compite con el arranque de la app
  // y en conexiones lentas / auditorías la descarga se corta (errores de conexión en consola).
  // Se monta cuando la página ya ha terminado de cargar: el hero se pinta al instante en oscuro
  // y el vídeo entra un momento después.
  useEffect(() => {
    let id;
    const start = () => {
      // En conexiones lentas o con "ahorro de datos" activo no bajamos los 24 MB del vídeo:
      // el hero se queda en oscuro (con sus degradados) en lugar de gastar la tarifa del visitante.
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const slow = conn && (conn.saveData || /^(slow-)?2g$|^3g$/.test(conn.effectiveType || ''));
      if (!slow) setPlayVideo(true);
    };
    const onLoad = () => {
      id = window.requestIdleCallback ? window.requestIdleCallback(start, { timeout: 1200 }) : setTimeout(start, 600);
    };
    if (document.readyState === 'complete') onLoad();
    else window.addEventListener('load', onLoad, { once: true });
    return () => {
      window.removeEventListener('load', onLoad);
      if (id !== undefined) {
        if (window.cancelIdleCallback) window.cancelIdleCallback(id); else clearTimeout(id);
      }
    };
  }, []);

  // El vídeo pesa 24 MB y el navegador se lo descarga entero mientras está en marcha,
  // dejando sin ancho de banda a las imágenes del resto de la página (que se ven llegar
  // con retraso). Al salir del hero se pausa: se libera la conexión y las imágenes cargan
  // al instante; al volver arriba, el vídeo continúa donde estaba.
  useEffect(() => {
    if (!playVideo) return;
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) video.play().catch(() => {});
      else video.pause();
    }, { threshold: 0 });
    observer.observe(video);
    return () => observer.disconnect();
  }, [playVideo]);

  const scrollDown = () => {
    document.querySelector('#sectores')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="inicio" className="relative min-h-screen flex items-center overflow-hidden bg-brand-dark">

      {/* Background video */}
      {playVideo && (
        <video
          ref={videoRef}
          src={HERO_VIDEO}
          className="absolute inset-0 w-full h-full object-cover opacity-80"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata" />
      )}

      {/* Gradient overlay for readability (más oscuro en móvil, donde sí hay texto encima) */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-dark/80 via-brand-dark/50 to-brand-dark/80 lg:from-brand-dark/50 lg:via-brand-dark/20 lg:to-brand-dark/50" />
      <div className="absolute inset-0 bg-black/30 lg:bg-black/10" />

      {/* Texto del hero: solo en móvil. En escritorio el vídeo se queda limpio. */}
      <div className="relative z-10 lg:hidden w-full max-w-7xl mx-auto px-6 pt-28 pb-20 min-h-screen flex flex-col justify-center">
        <h1 className="font-heading font-semibold text-3xl leading-[1.15] tracking-tight text-white text-balance">
          {t('hero.title')}
        </h1>
        <p className="brand-gradient-text text-lg font-semibold leading-relaxed max-w-xl mt-4">
          {t('hero.desc2')}
        </p>

        <div className="flex items-center gap-2.5 h-9 mt-5">
          <span className="text-white/60 font-light text-base">{t('hero.for')}</span>
          <div className="overflow-hidden h-9" aria-hidden="true">
            <div
              className="transition-transform duration-500"
              style={{ transform: `translateY(-${currentSector * 36}px)` }}>
              {sectors.map(({ name, icon: Icon }) =>
              <div key={name} className="h-9 flex items-center gap-2">
                <Icon size={18} className="text-brand-green" />
                <span className="text-brand-green font-medium text-base">{name}</span>
              </div>
              )}
            </div>
          </div>
          <span className="sr-only" aria-live="polite">{sectors[currentSector]?.name}</span>
        </div>

        <div className="grid grid-cols-2 gap-5 pt-10 mt-10">
          {stats.map((stat) =>
          <div key={stat.label} className="flex items-center gap-2.5">
            <stat.icon size={20} style={{ color: stat.color }} />
            <div>
              <div className="font-heading font-semibold text-xl text-white"><CountUp value={stat.value} /></div>
              <div className="text-white/50 text-xs font-light">{stat.label}</div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollDown}
        aria-label="Desplazarse a la sección de sectores"
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-white/50 hover:text-white transition-colors">
        <span className="text-xs font-medium tracking-widest uppercase">{t('hero.discoverMore')}</span>
        <ChevronDown size={20} className="animate-bounce" />
      </button>

    </section>
  );
}